import React, { useState } from 'react';
import {
  useSubmitIncidentMutation,
  useDiscardIncidentDraftMutation,
  useWithdrawIncidentMutation,
  useCloseIncidentMutation,
  useChangeIncidentSeverityMutation,
} from '../../store/api/localApi';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import {
  IncidentSeverity,
  NotifiableCategory,
  StaffIncident,
  StaffIncidentStatus,
} from '../../types';
import { getSeverityChipConfig } from './IncidentSeverityChip';

interface Props {
  incident: StaffIncident;
}

const NOTIFIABLE_CATEGORY_LABELS: Record<NotifiableCategory, string> = {
  [NotifiableCategory.SERIOUS_INJURY_HOSPITAL]: 'Serious injury (hospitalisation)',
  [NotifiableCategory.INFECTIOUS_DISEASE]: 'Infectious disease',
  [NotifiableCategory.CHILD_MISSING]: 'Child missing',
  [NotifiableCategory.UNPLANNED_CLOSURE]: 'Unplanned closure',
  [NotifiableCategory.DEATH]: 'Death',
  [NotifiableCategory.OTHER_NOTIFIABLE]: 'Other notifiable',
};

/** Pull the backend's human error message out of an RTK Query error so the
 *  notifiable-close-blocked case (and any other 4xx/409) surfaces verbatim in
 *  a toast rather than being swallowed. Mirrors IncidentTuslaPanel's handler. */
function backendMessage(err: unknown, fallback: string): string {
  const data =
    err && typeof err === 'object' && 'data' in err
      ? (err as { data?: { error?: string; message?: string } }).data
      : undefined;
  // TUSLA_REPORT_REQUIRED is the machine code the close handler returns for a
  // NOTIFIABLE incident with no tuslaReportedAt — translate it to the guidance
  // the task asks for rather than showing a raw enum to the operator.
  if (data?.error === 'TUSLA_REPORT_REQUIRED') {
    return 'Mark this incident as reported to Tusla before closing it.';
  }
  return data?.message || data?.error || fallback;
}

/**
 * Admin-only action toolbar for the staff incident detail. Every control here
 * is `incident:manage`/`incident:submit`/`incident:discard-draft` server-side
 * — admin-level — so the whole toolbar is gated on `isAdmin` (a STAFF/PARENT
 * viewer sees nothing, matching IncidentTuslaPanel's gating). The backend is
 * still the enforcer; this only hides actions the caller could not take.
 *
 * Buttons are gated by the incident's current status so an invalid transition
 * is never offered:
 *   DRAFT     → Submit, Discard draft (hard delete — destructive confirm)
 *   SUBMITTED → Close, Withdraw (reason), Change severity (severity + reason)
 *   CLOSED    → Change severity only (escalation reopens it to SUBMITTED)
 *   WITHDRAWN → nothing (terminal)
 *
 * The notifiable-close prerequisite is not re-derived here as a hard block:
 * Close stays enabled and, if the backend refuses (409 TUSLA_REPORT_REQUIRED
 * because a NOTIFIABLE incident has no tuslaReportedAt), the error is surfaced
 * in a toast that tells the operator to mark it reported first (the
 * "Mark as reported…" action lives in the adjacent IncidentTuslaPanel).
 */
const IncidentActions: React.FC<Props> = ({ incident }) => {
  const { isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const [submitIncident, { isLoading: submitting }] = useSubmitIncidentMutation();
  const [discardDraft, { isLoading: discarding }] = useDiscardIncidentDraftMutation();
  const [withdrawIncident, { isLoading: withdrawing }] = useWithdrawIncidentMutation();
  const [closeIncident, { isLoading: closing }] = useCloseIncidentMutation();
  const [changeSeverity, { isLoading: changingSeverity }] = useChangeIncidentSeverityMutation();

  const [confirm, setConfirm] = useState<null | 'submit' | 'discard' | 'close'>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [severityOpen, setSeverityOpen] = useState(false);

  const [withdrawReason, setWithdrawReason] = useState('');
  const [severityTo, setSeverityTo] = useState<IncidentSeverity>(incident.severity);
  const [severityCategory, setSeverityCategory] = useState<NotifiableCategory>(
    NotifiableCategory.SERIOUS_INJURY_HOSPITAL
  );
  const [severityReason, setSeverityReason] = useState('');

  // Admin-only surface: STAFF/PARENT never see any of these.
  if (!isAdmin) return null;

  const status = incident.status;
  const isDraft = status === StaffIncidentStatus.DRAFT;
  const isSubmitted = status === StaffIncidentStatus.SUBMITTED;
  const isClosed = status === StaffIncidentStatus.CLOSED;

  // WITHDRAWN (or any other terminal/unknown state) offers nothing.
  if (!isDraft && !isSubmitted && !isClosed) return null;

  const handleSubmit = async () => {
    try {
      await submitIncident({ incidentId: incident.id }).unwrap();
      showSuccess('Incident submitted.', 'incident-action-toast');
      setConfirm(null);
    } catch (err) {
      showError(backendMessage(err, 'Failed to submit the incident.'), 'incident-action-toast');
    }
  };

  const handleDiscard = async () => {
    try {
      await discardDraft({ incidentId: incident.id }).unwrap();
      showSuccess('Draft discarded.', 'incident-action-toast');
      setConfirm(null);
    } catch (err) {
      showError(backendMessage(err, 'Failed to discard the draft.'), 'incident-action-toast');
    }
  };

  const handleClose = async () => {
    try {
      await closeIncident({ incidentId: incident.id }).unwrap();
      showSuccess('Incident closed.', 'incident-action-toast');
      setConfirm(null);
    } catch (err) {
      // Surfaces TUSLA_REPORT_REQUIRED (notifiable-close-blocked) verbatim.
      showError(backendMessage(err, 'Failed to close the incident.'), 'incident-action-toast');
    }
  };

  const handleWithdraw = async () => {
    const reason = withdrawReason.trim();
    if (!reason) return;
    try {
      await withdrawIncident({ incidentId: incident.id, reason }).unwrap();
      showSuccess('Incident withdrawn.', 'incident-action-toast');
      setWithdrawOpen(false);
      setWithdrawReason('');
    } catch (err) {
      showError(backendMessage(err, 'Failed to withdraw the incident.'), 'incident-action-toast');
    }
  };

  const handleChangeSeverity = async () => {
    const reason = severityReason.trim();
    if (!reason) return;
    try {
      await changeSeverity({
        incidentId: incident.id,
        to: severityTo,
        notifiableCategory:
          severityTo === IncidentSeverity.NOTIFIABLE ? severityCategory : undefined,
        reason,
      }).unwrap();
      showSuccess('Severity updated.', 'incident-action-toast');
      setSeverityOpen(false);
      setSeverityReason('');
    } catch (err) {
      showError(backendMessage(err, 'Failed to change severity.'), 'incident-action-toast');
    }
  };

  const btn = 'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium';
  const neutralBtn = `${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`;
  const primaryBtn = `${btn} border-transparent bg-primary-600 text-white hover:bg-primary-700`;
  const dangerBtn = `${btn} border-red-300 bg-white text-red-700 hover:bg-red-50`;

  return (
    <div
      className="incident-print-only-hide flex flex-wrap items-center gap-2"
      data-testid="incident-actions"
    >
      {isDraft && (
        <>
          <button
            type="button"
            data-testid="incident-submit-btn"
            onClick={() => setConfirm('submit')}
            className={primaryBtn}
          >
            Submit
          </button>
          <button
            type="button"
            data-testid="incident-discard-btn"
            onClick={() => setConfirm('discard')}
            className={dangerBtn}
          >
            Discard draft
          </button>
        </>
      )}

      {isSubmitted && (
        <>
          <button
            type="button"
            data-testid="incident-close-btn"
            onClick={() => setConfirm('close')}
            className={primaryBtn}
          >
            Close
          </button>
          <button
            type="button"
            data-testid="incident-withdraw-btn"
            onClick={() => setWithdrawOpen(true)}
            className={neutralBtn}
          >
            Withdraw
          </button>
        </>
      )}

      {(isSubmitted || isClosed) && (
        <button
          type="button"
          data-testid="incident-change-severity-btn"
          onClick={() => {
            setSeverityTo(incident.severity);
            setSeverityReason('');
            setSeverityOpen(true);
          }}
          className={neutralBtn}
        >
          Change severity
        </button>
      )}

      {/* Submit — simple confirm. */}
      <ConfirmDialog
        isOpen={confirm === 'submit'}
        title="Submit this incident?"
        message="Submitting turns this draft into a statutory record and notifies the child's guardians. It can no longer be deleted."
        confirmLabel="Submit"
        variant="default"
        isLoading={submitting}
        confirmTestId="incident-submit-confirm"
        onCancel={() => setConfirm(null)}
        onConfirm={handleSubmit}
      />

      {/* Discard — destructive confirm (hard delete). */}
      <ConfirmDialog
        isOpen={confirm === 'discard'}
        title="Discard this draft?"
        message="This permanently deletes the draft. This cannot be undone."
        confirmLabel="Discard draft"
        variant="danger"
        isLoading={discarding}
        confirmTestId="incident-discard-confirm"
        onCancel={() => setConfirm(null)}
        onConfirm={handleDiscard}
      />

      {/* Close — simple confirm. Notifiable-close prerequisite is enforced by
          the backend; a refusal surfaces via the error toast. */}
      <ConfirmDialog
        isOpen={confirm === 'close'}
        title="Close this incident?"
        message="Closing marks the incident as handled. A notifiable incident cannot be closed until it has been reported to Tusla."
        confirmLabel="Close incident"
        variant="default"
        isLoading={closing}
        confirmTestId="incident-close-confirm"
        onCancel={() => setConfirm(null)}
        onConfirm={handleClose}
      />

      {/* Withdraw — requires a reason. */}
      <ConfirmDialog
        isOpen={withdrawOpen}
        title="Withdraw this incident?"
        message="The record stays in the register flagged as withdrawn, with your reason. Give a reason for the retraction."
        confirmLabel="Withdraw"
        variant="warning"
        isLoading={withdrawing}
        confirmTestId="incident-withdraw-confirm"
        onCancel={() => {
          setWithdrawOpen(false);
          setWithdrawReason('');
        }}
        onConfirm={handleWithdraw}
      >
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            data-testid="incident-withdraw-reason"
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
            rows={3}
            className="block w-full rounded-md border-gray-300 text-sm"
            placeholder="Why is this incident being withdrawn?"
          />
        </div>
      </ConfirmDialog>

      {/* Change severity — severity select + (if NOTIFIABLE) category + reason. */}
      <ConfirmDialog
        isOpen={severityOpen}
        title="Change severity"
        message={
          isClosed
            ? 'Escalating a closed incident reopens it for follow-up. Give a reason for the change.'
            : 'Give a reason for the severity change — it is recorded on the incident.'
        }
        confirmLabel="Save change"
        variant="warning"
        isLoading={changingSeverity}
        confirmTestId="incident-change-severity-confirm"
        onCancel={() => {
          setSeverityOpen(false);
          setSeverityReason('');
        }}
        onConfirm={handleChangeSeverity}
      >
        <div className="mt-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New severity</label>
            <select
              data-testid="incident-severity-select"
              value={severityTo}
              onChange={(e) => setSeverityTo(e.target.value as IncidentSeverity)}
              className="block w-full rounded-md border-gray-300 text-sm"
            >
              {Object.values(IncidentSeverity).map((sev) => (
                <option key={sev} value={sev}>
                  {getSeverityChipConfig(sev).label}
                </option>
              ))}
            </select>
          </div>

          {severityTo === IncidentSeverity.NOTIFIABLE && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notifiable category <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="incident-notifiable-category-select"
                value={severityCategory}
                onChange={(e) => setSeverityCategory(e.target.value as NotifiableCategory)}
                className="block w-full rounded-md border-gray-300 text-sm"
              >
                {Object.values(NotifiableCategory).map((cat) => (
                  <option key={cat} value={cat}>
                    {NOTIFIABLE_CATEGORY_LABELS[cat]}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              data-testid="incident-severity-reason"
              value={severityReason}
              onChange={(e) => setSeverityReason(e.target.value)}
              rows={3}
              className="block w-full rounded-md border-gray-300 text-sm"
              placeholder="Why is the severity changing?"
            />
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default IncidentActions;
